import { DreamTopic } from '../types';

interface CustomEnrichment {
  quickAnswer?: string;
  scholarlyNuances?: string;
  comparisonTable?: {
    aspect: string;
    goodMeaning: string;
    warningMeaning: string;
    context: string;
  }[];
  practicalAdvice?: {
    title: string;
    advice: string;
  }[];
  commonMistakes?: string[];
  expandedIbnSirin?: string;
  expandedNabulsi?: string;
  expandedCases?: {
    single?: string;
    married?: string;
    pregnant?: string;
    divorced?: string;
    man?: string;
  };
}

const CUSTOM_DREAM_ENRICHMENTS: Record<string, CustomEnrichment> = {
  snake: {
    quickAnswer: 'يدل حلم الثعبان في المنام غالباً على وجود عدو حاسد أو ماكر في محيط الرائي، وتزداد خطورة هذا العدو بمقدار ضخامة الثعبان وسواده وسُمِّه. إذا قتله الرائي أو سيطر عليه نال نصراً وظفراً كبيراً، وإذا لدغه ناله مكروه أو تعطل في مساعيه بقدر شدة اللدغة والألم.',
    scholarlyNuances: 'استنبط الإمام محمد بن سيرين دلالة الحية أو الثعبان على العداوة من قصة إخراج إبليس لآدم وحواء عليهما السلام من الجنة متوسلاً بالحية. وأكد ابن شاهين الظاهري أن الثعابين التي تسكن البيوت تدل على أعداء من ذوي القربى والمخالطين (الأقارب أو أهل المنزل)، بينما ثعابين البراري والفيافي ترمز إلى أعداء غرباء لا صلة قرابة بينك وبينهم. كما ميّز أئمة التعبير بين الثعبان كرمز للعداوة وبين رؤيته بلا أذى، فمن رأى ثعباناً يطيعه دون أن يخافه أو يؤذيه نال ملكاً وعزة وسلطاناً ورزقاً واسعاً.',
    comparisonTable: [
      {
        aspect: 'الثعبان الأسود الضخم',
        goodMeaning: 'الظفر والنصر المبين إن تمكن الرائي من قتله أو قطع رأسه.',
        warningMeaning: 'ألدّ الأعداء وأشدهم مكراً وسحراً وبغضاء، أو خصومة مع صاحب نفوذ وظلم.',
        context: 'إذا شوهد في البيت أو كان يلاحق الرائي بحدة وعينين بارزتين.'
      },
      {
        aspect: 'الثعبان الأخضر المائي',
        goodMeaning: 'رزق مالي ووفير قادم، أو منفعة من شخص ذي جاه وهيئة.',
        warningMeaning: 'عدو يتستر وراء الدين أو التظاهر بالصلاح والنصح وهو يضمر الخديعة.',
        context: 'إذا كان هادئاً ولم يبادر بالهجوم أو العض.'
      },
      {
        aspect: 'الثعبان الأصفر الصغير أو الكبير',
        goodMeaning: 'الشفاء من علة مستعصية وزوال عين حاسدة إن قتله الرائي.',
        warningMeaning: 'وعكة صحية، خيبة أمل، وحسد شديد متوجه لنعم الرائي وأمواله.',
        context: 'يرتبط مباشرة بوهن الجسد أو الحسد بين الأقران في العمل أو الدراسة.'
      },
      {
        aspect: 'لدغة الثعبان في اليد أو القدم',
        goodMeaning: 'إذا كانت اللدغة في اليد اليمنى تدل على مال ورزق يأتي بعد تعب وجهد.',
        warningMeaning: 'إن كانت في اليد اليسرى فتنبه للابتعاد عن الشبهات والذنوب، وفي القدم تعطل في السفر أو العمل.',
        context: 'تتوقف الدلالة على موضع اللدغة ومقدار السم والألم الناتج عنها.'
      },
      {
        aspect: 'قتل الثعبان والنجاة منه',
        goodMeaning: 'بشارة حاسمة بالنصر على الأعداء، قضاء الديون، الخلاص من الحسد والأسحار، وسلامة العاقبة.',
        warningMeaning: 'لا يوجد تحذير في قتل الثعبان المؤذي باتفاق كافة المفسرين.',
        context: 'رؤية محمودة تشرح الصدر وتؤذن بزوال الغمة نهائياً.'
      }
    ],
    commonMistakes: [
      'الظن بأن كل ثعبان في المنام هو بالضرورة سحر أو مس شيطاني، في حين أن معظمها أعداء واقعيون أو حساد أو مجرد ضغوط نفسية.',
      'تجاهل لون الثعبان وموضعه؛ فالثعبان في الفراش يختلف تماماً عن الثعبان في مكان العمل أو في الحقل الزراعي.',
      'الفزع الشديد واليأس بعد رؤية الثعبان؛ فالقرآن الكريم جعل عصا موسى عليه السلام تنقلب حية تسعى بأمر الله لنصرة الحق.'
    ]
  },

  gold: {
    quickAnswer: 'تفسير حلم الذهب يحمل وجهين متناقضين: فهو للنساء من أرقى الرؤى الدالة على الزينة، الزواج المبارك، الحمل، وسعة الرزق والاستقرار؛ بينما يكرهه جمهور المعبرين للرجال إذا كان ملبوساً لاشتقاقه من "الذهاب" ولونه الأصفر الدال على الهم والحزن والإنفاق الإجباري.',
    scholarlyNuances: 'فرّق الإمام النابلسي وابن سيرين بين الذهب المصوغ والذهب الخام (السبائك والتبر)؛ فالذهب المصوغ كالخواتم والسلاسل والأساور أقل شراً للرجال وبشارة تامة للنساء، بينما السبائك تدل على الغرامات والهموم بعدد ما أصاب منها. واستثنى المفسرون أخذ الذهب من سلطان أو الحصول على دنانير ذهبية قديمة، إذ تدل على تولي مناصب شريفة ونيل هيبة اجتماعية واسعة.',
    comparisonTable: [
      {
        aspect: 'لبس الذهب للمرأة (خاتم، قلادة، أساور)',
        goodMeaning: 'زواج للأعزب، حمل للمتزوجة، زينة، رفعة قدر، وسرور يدخل البيت.',
        warningMeaning: 'ضياعه أو انكساره يدل على فرقة عاطفية أو فقدان حلي عزيز.',
        context: 'إذا كان الذهب براقاً نظيفاً وتشعر الرائية بالفرح والاعتزاز.'
      },
      {
        aspect: 'لبس الذهب للرجل (سوار أو خاتم ذهب)',
        goodMeaning: 'لا يُحمد لبسه، إلا أن يرث مالاً أو يصاهر قوماً غير أكفاء.',
        warningMeaning: 'هم، حزن، ذهاب مال، وربما ارتكاب ما يخالف السنة والوقوع في شبهات مالية.',
        context: 'التحريم الشرعي للباس الذهب على الرجال ينعكس رمزياً في عالم الرؤى بالهموم.'
      },
      {
        aspect: 'العثور على سبيكة ذهب مجهولة',
        goodMeaning: 'العثور عليها مدفونة في أرض الرائي قد يدل على كنز علمي أو ميراث قديم.',
        warningMeaning: 'غرم مالي أو هم بقدر وزن السبيكة يأتيه من جهة مسؤول أو ضريبة أو نزاع.',
        context: 'السبائك الخام ترمز للثقل والعبء النفسي والمالي.'
      },
      {
        aspect: 'إعطاء الميت ذهباً للحي',
        goodMeaning: 'من أسمى البشارات: علو شأن، نجاة من كرب عظيم، رزق مبارك، ورضا من الله تعالى.',
        warningMeaning: 'إذا أخذ الميت الذهب من الحي فقد يدل على خسارة تجارية أو فقد عزيز.',
        context: 'عطية الميت دائماً خير وبركة ونماء في التعبير.'
      }
    ]
  },

  death: {
    quickAnswer: 'الموت في المنام نادراً ما يعني الوفاة الحقيقية؛ بل هو في أصول التعبير رمز للتوبة من ذنب عظيم، أو طول العمر، أو الشفاء من مرض، أو سداد الديون، أو الانتقال من مرحلة قديمة إلى مرحلة جديدة أكثر بهاءً وتوفيقاً.',
    scholarlyNuances: 'استند ابن سيرين في تأويل الموت بالتوبة وطول العمر إلى قوله تعالى: ﴿أَوَمَن كَانَ مَيْتًا فَأَحْيَيْنَاهُ وَجَعَلْنَا لَهُ نُورًا يَمْشِي بِهِ فِي النَّاسِ﴾. وأكد ابن شاهين أن الموت المجرد من مظاهر العويل واللطم والنياحة هو عمر مديد وسلامة وصحة، أما إذا صاحبه شق الجيوب والصراخ وسكب التراب على الرؤوس فهو فساد في الدين ونقص في الجاه وتنبيه من مصيبة دنيوية.',
    comparisonTable: [
      {
        aspect: 'موت الرائي نفسه دون مرض ولا جنازة',
        goodMeaning: 'طول في العمر، صحة في البدن، وتوبة نصوح من خطيئة يتبعها هدى وصلاح.',
        warningMeaning: 'إن مات ورأى قبره يُسد عليه وهو مستسلم فقد يدل على قسوة قلبه وغفلته.',
        context: 'إذا استيقظ الرائي شاعراً بالراحة والسلام الداخلي.'
      },
      {
        aspect: 'موت شخص حي والبكاء عليه بلا صراخ',
        goodMeaning: 'طول عمر للشخص المذكور، تفريج كربته، وزوال هم ثقيل كان يحمله.',
        warningMeaning: 'إن كان البكاء عويلاً ولطماً فهو نزول بلاء بذلك الشخص أو بأهله.',
        context: 'الدموع الباردة بلا صوت فرج وسرور، والنياحة حزن.'
      },
      {
        aspect: 'عودة الميت إلى الحياة في المنام',
        goodMeaning: 'إحياء أمر ميؤوس منه، رجوع غائب، استرداد حق قديم، وحسن حال الميت عند ربه.',
        warningMeaning: 'لا يوجد تحذير؛ فرؤية الميت حياً من أصدق الرؤى وأكثرها إشراقاً.',
        context: 'الميت في دار الحق، وما يقوله في المنام حق وصدق.'
      }
    ]
  },

  'teeth-falling': {
    quickAnswer: 'تساقط الأسنان في المنام يرمز إلى الأهل والقرابة وطول عمر الرائي. إذا سقطت السن في اليد أو الحجر أو الثوب فهي بشارة بطول العمر، قضاء الديون، أو قدوم مولود جديد؛ أما إذا سقطت على الأرض وغابت عن العين فتنذر بفقد أو مرض يلحق بأحد أفراد العائلة.',
    scholarlyNuances: 'قسم المعبرون الفم إلى شطرين: الأسنان العلوية تمثل رجال العائلة من جهة الأب (الثنايا الأب والعم، والرباعيات أبناء العم)، والسفلية تمثل نساء العائلة من جهة الأم (الثنايا الأم والخالة، والأضراس كبار السن والأجداد). وسقوط الأسنان كلها دون أن يراها الرائي تأويله أنه يعيش حتى يشهد أقرانه، وهو دليل طول العمر.',
    comparisonTable: [
      {
        aspect: 'سقوط السن في الكف أو الحجر',
        goodMeaning: 'طول عمر، رزق بمال وفير، صلح مع قريب، أو ولادة غلام صالح.',
        warningMeaning: 'لا يوجد تحذير، بل هي رؤيا مبشرة ومحمودة للغاية.',
        context: 'سقوط السن دون ألم أو نزيف حاد في يد الرائي.'
      },
      {
        aspect: 'سقوط السن على الأرض والتراب واختفاؤه',
        goodMeaning: 'قد يدل على تخلص الرائي من عبء مالي ثقيل إن كان مديوناً.',
        warningMeaning: 'مرض يصيب أحد الأقارب أو فراق أو انقطاع صلة رحم.',
        context: 'إذا بحث الرائي عن السن في التراب ولم يجده أو شعر بالحزن عليه.'
      },
      {
        aspect: 'سقوط الضرس المسوس أو التالف',
        goodMeaning: 'الشفاء من علة مزمنة، انقضاء مشكلة عائلية معقدة، وقطع صلة بشخص مؤذٍ وفاسد.',
        warningMeaning: 'لا تحذير فيه؛ فسقوط السوس زوال للشر والخبث.',
        context: 'شعور الرائي براحة فورية بعد سقوط الضرس المعطوب.'
      }
    ]
  },

  sea: {
    quickAnswer: 'البحر في المنام يرمز إلى السلطان العظيم ذي الهيبة، أو الدنيا بأهوالها وتقلباتها، أو العلم الغزير والتجارة الواسعة. البحر الهادئ الصافي سكينة وسعة أرزاق ونيل أمنيات، بينما البحر الهائج فتن وتحديات تتطلب الحكمة والصبر.',
    scholarlyNuances: 'قال الإمام النابلسي إن أمواج البحر جنود السلطان أو عوارض الدنيا، فمن خاض البحر وسبح فيه بمهارة نال من العلم ما لم ينله غيره وترقى في عمله، ومن شرب من ماء البحر الصافي كُتب له مال وسلطان مديد، ومن غرق في البحر ثم نجا بتضرع واستغاثة تاب من الذنوب وتخلص من ورطة كبرى كادت تعصف بمستقبله.',
    comparisonTable: [
      {
        aspect: 'البحر الهادئ الصافي والشمس مشرقة',
        goodMeaning: 'استقرار أسري ونفسي، سعة رزق، هدوء البال، ونيل درجات علمية أو مناصب عليا.',
        warningMeaning: 'لا تحذير فيه إلا إذا شعر الرائي بالوحشة والعزلة المطلقة.',
        context: 'رؤية مياه البحر زرقاء نقية تعكس السماء بوضوح.'
      },
      {
        aspect: 'البحر الهائج بأمواج عاتية',
        goodMeaning: 'الصمود أمام التحديات واكتساب خبرات حياتية صلبة.',
        warningMeaning: 'فتن، تقلبات اقتصادية، خصومات حادة، أو اضطراب نفسي وقلق من المستقبل.',
        context: 'إذا كانت الأمواج تضرب الشواطئ بقوة وتثير الرعب.'
      },
      {
        aspect: 'السباحة بمهارة ويسر في عمق البحر',
        goodMeaning: 'التمكن من التجارة، إتقان العلوم، وتحقيق إنجاز كبير يفوق التوقعات.',
        warningMeaning: 'إن ثقلت حركته أو غاص دون تنفس فعليه مراجعة حساباته ومصروفاته.',
        context: 'الشعور بالخفة والانطلاق في الماء.'
      },
      {
        aspect: 'الغرق في البحر والنجاة منه بعد مشقة',
        goodMeaning: 'النجاة من مكيدة، الخروج من أزمة مالية أو صحية، والتوبة الخالصة لله.',
        warningMeaning: 'الغرق دون نجاة يدل على الانغماس التام في شهوات الدنيا والغفلة عن الآخرة.',
        context: 'وصول الرائي إلى الشاطئ سالماً معافى في ختام الرؤيا.'
      }
    ]
  },

  marriage: {
    quickAnswer: 'تفسير حلم الزواج في المنام من أروع البشارات الدالة على العناية الإلهية، والستر، وتولي المسؤوليات الشريفة، وسعة الرزق وقضاء الحوائج؛ وتأويله للأعزب زواج حقيقي قريب، وللمتزوج رفعة في الشأن وتجدد للمودة والبركة في الذرية.',
    scholarlyNuances: 'أصل الزواج في لغة العرب والنواة الدلالية عند ابن سيرين هو الاقتران والارتباط والشراكة، ولذلك يرمز زواج المرء إلى شراكته في عمل مبارك أو نيله عهداً جديداً. ونبه النابلسي إلى أن العرس الخالي من المزامير والغناء والرقص هو بركة محضة وخير وسرور، أما إذا صاحبه المعازف والصراخ فإنه ينقلب إلى دلالة على المصيبة والهم المؤقت.',
    comparisonTable: [
      {
        aspect: 'الزواج بدون غناء ولا معازف',
        goodMeaning: 'بركة تامة، رفعة، رزق حلال، صلح بين متخاصمين، وهناء يدخل البيوت.',
        warningMeaning: 'لا تحذير فيه باتفاق أهل التعبير.',
        context: 'عقد قران بهدوء وسكينة وتبادل للبشارات.'
      },
      {
        aspect: 'الزواج من امرأة مجهولة حسناء',
        goodMeaning: 'نيل دنيا مقبلة، تولي ولاية أو ترقية وظيفية، وتيسير أمر كان مستعصياً.',
        warningMeaning: 'إن كانت المرأة قبيحة أو مريضة فقد يدل على متاعب مؤقتة.',
        context: 'جمال العروس يعبر عن بهاء الدنيا وحسن العاقبة.'
      },
      {
        aspect: 'زواج المتزوجة من زوجها مرة ثانية',
        goodMeaning: 'تجدد الحب، انتهاء الخلافات، بشارة بحمل قريب، أو شراء بيت جديد.',
        warningMeaning: 'لا تحذير فيه بل هو تأكيد للألفة والوفاق.',
        context: 'رؤية ارتداء فستان العرس مجدداً بابتسامة ورضا.'
      }
    ]
  },

  shoes: {
    quickAnswer: 'الحذاء في المنام يرمز إلى السفر، والعمل، والوقاية، والزوج أو الشريك العاطفي؛ فالحذاء الجديد المريح يدل على وظيفة مرموقة أو زواج سعيد ومستقر، بينما ضياع الحذاء أو انقطاعه ينذر بفراق عارض أو تعطل في مسعى سفر وعمل.',
    scholarlyNuances: 'فسر ابن سيرين المشي في نعلين بالسفر والترحال في طلب الرزق، فإذا انقطع شسع أحدهما أقام الرائي في سفره لعارض. ولون الحذاء له دلالات دقيقة: الأسود سيادة ومال، والأبيض صفاء ونقاء وزواج من شخص تقي، والأحمر متعة وزينة، والأخضر زهد وصلاح وورع، والأصفر مرض أو هم.',
    comparisonTable: [
      {
        aspect: 'لبس حذاء جديد مريح وأنيق',
        goodMeaning: 'بداية عمل جديد مرموق، خطوبة وزواج، استقرار معيشي، وتوفيق في المساعي.',
        warningMeaning: 'إن كان ضيقاً يعصر القدم دل على مسؤوليات تفوق طاقة الرائي.',
        context: 'المشي بالحذاء الجديد بخفة وثقة.'
      },
      {
        aspect: 'ضياع الحذاء والبحث عنه حافياً',
        goodMeaning: 'العثور عليه بعد بحث يدل على استرداد ما فُقد وعودة الغائب.',
        warningMeaning: 'خسارة فرصة عمل، نزاع عاطفي، أو ضياع مال بسبب الإهمال.',
        context: 'البحث المستمر في أماكن مزدحمة أو في الوحل.'
      },
      {
        aspect: 'شراء حذاء أبيض أو أسود فخم',
        goodMeaning: 'الحذاء الأسود هيبة ونفوذ، والحذاء الأبيض ارتباط برجل أو امرأة ذات خلق ودين.',
        warningMeaning: 'الحذاء الممزق أو المتسخ بالوحل تنبيه لتنظيف السيرة وتجنب الشبهات.',
        context: 'تجربة قياس الحذاء وانسجامه مع القدم.'
      }
    ]
  },

  money: {
    quickAnswer: 'تفسير الفلوس والنقود في المنام يختلف بحسب نوعها؛ فالنقود الورقية الجديدة رزق وفير وأمانة وبركة وأخبار سارة مؤجلة، بينما النقود المعدنية تعبر عن الكلام والتسبيح أو الخصومات الكلامية العابرة إن كان لها رنين مزعج.',
    scholarlyNuances: 'استنبط ابن سيرين الفلوس الورقية المعاصرة من معنى الدنانير والصكوك القديمة، فمن وجد صرة نقود أتاه رزق من حيث لا يحتسب أو اؤتمن على سر عظيم. وحذر النابلسي من عدّ النقود ونقصانها في الحلم، إذ يشير إلى إنفاق المال في غير محله والشعور بالندم لاحقاً.',
    comparisonTable: [
      {
        aspect: 'العثور على رزم نقود ورقية خضراء أو زرقاء',
        goodMeaning: 'أرباح تجارية، انفراج أزمة مالية خانقة، وتحقيق طموح استثماري كبير.',
        warningMeaning: 'تتطلب من الرائي إخراج الزكاة وحسن إدارة الموارد وتجنب التبذير.',
        context: 'العثور عليها في حقيبة أو مكان نظيف دون شبهة سرقة.'
      },
      {
        aspect: 'إعطاء المال للمحتاجين والفقراء في المنام',
        goodMeaning: 'من أعظم دلالات البركة وقبول العمل الصالح ودفع البلاء وزيادة العطاء في الواقع.',
        warningMeaning: 'لا يوجد تحذير في الصدقة والعطاء المحمود.',
        context: 'شعور الرائي بالسعادة والبذل الخالص.'
      },
      {
        aspect: 'أخذ الفلوس من شخص ميت',
        goodMeaning: 'رزق غير متوقع، ميراث، أو فتح باب عمل كان موصداً.',
        warningMeaning: 'إعطاء الميت فلوساً للحي أفضل من إعطاء الحي للميت في أغلب التفاسير.',
        context: 'الميت يبتسم ويسلم النقود باليد اليمنى.'
      }
    ]
  },

  rain: {
    quickAnswer: 'المطر في المنام هو الغيث والرحمة وإحياء الأرض بعد موتها؛ وهو دلالة صريحة على استجابة الدعاء، وتفريج الكروب، وسعة الرزق، وبرء الأسقام للمريض، ما لم يكن طوفاناً مدمراً أو هادماً للبيوت.',
    scholarlyNuances: 'استدل المعبرون بقوله تعالى: ﴿وَهُوَ الَّذِي يُنَزِّلُ الْغَيْثَ مِن بَعْدِ مَا قَنَطُوا وَيَنشُرُ رَحْمَتَهُ﴾ على أن المطر فرج بعد يأس. وأوضح ابن سيرين أن المطر العام النافع بركة ورخاء شامل للأمة والبلاد، بينما المطر الذي يسقط على دار الرائي وحدها قد يرمز إلى رزق خاص به أو هم ينفرد به بحسب صفة الماء.',
    comparisonTable: [
      {
        aspect: 'المطر الخفيف الهادئ والصافي',
        goodMeaning: 'استجابة دعوات طال انتظارها، شفاء، راحة بال، وتيسير في الرزق والحمل.',
        warningMeaning: 'لا تحذير فيه إطلاقاً، وهو غيث مبارك.',
        context: 'السير تحت المطر الخفيف برضا وانشراح صدر.'
      },
      {
        aspect: 'شرب ماء المطر العذب النقي',
        goodMeaning: 'نيل حكمة، علم نافع، شفاء من كل داء، وصلاح في الدين والدنيا.',
        warningMeaning: 'شرب الماء الكدر أو العكر تنبيه لمرض عارض أو كدر في العيش.',
        context: 'تجمع مياه المطر في إناء نظيف والشرب منه.'
      },
      {
        aspect: 'المطر الغزير المصحوب بالرعد والسيول المدمرة',
        goodMeaning: 'قوة هائلة وتغيرات جذرية تعيد ترتيب حياة الرائي للأفضل إن سلم منها.',
        warningMeaning: 'فتن، خسائر مادية، أو خوف من عقوبة صاحب سلطة إن هدم المطر الجدران.',
        context: 'حدوث انهيارات أو فيضانات تدخل المنازل.'
      }
    ]
  },

  pregnancy: {
    quickAnswer: 'الحمل في المنام يدل على سعة الدنيا، وزيادة في المال، والنعمة والجاه؛ وهو للمرأة بشارة بقدوم رزق ومسؤولية مباركة ومفرحة، بينما حمله للرجل هم ثقيل يخفيه عن الناس أو سعي شاق يثمر نجاحاً كبيراً بعد كبد.',
    scholarlyNuances: 'أكد الإمام ابن سيرين أن بطن الحامل في المنام هو وعاء الرزق، فكلما عظم بطنها اتسعت دنياها وزادت أموالها. وتأويل الحمل بالتوأم مضاعفة في الرزق والبشارات إن كانا بنتين، بينما ولادة الذكر فرج عاجل بعد مشقة.',
    comparisonTable: [
      {
        aspect: 'حمل المتزوجة وهي غير حامل في الواقع',
        goodMeaning: 'بشارة حقيقية بالحمل القريب، أو رزق يدخل بيتها، أو ترقية لزوجها.',
        warningMeaning: 'قد يكون انعكاساً لكثرة التفكير والرغبة الشديدة في الإنجاب (حديث نفس).',
        context: 'شعور بالبهجة وارتداء ملابس واسعة.'
      },
      {
        aspect: 'حمل الفتاة العزباء',
        goodMeaning: 'مشروع جديد كبير تخطط له وينجح، أو تفوق أكاديمي باهر بعد عناء.',
        warningMeaning: 'قد يدل على حمل هم أو هموم عائلية تثقل كاهلها وتفرج قريباً.',
        context: 'عدم وجود ولادة أو ألم مبرح في الحلم.'
      },
      {
        aspect: 'الحمل بتوأم (ولد وبنت أو بنتين)',
        goodMeaning: 'التوأم من الإناث دنيا مضاعفة وفرحة وسعادة غامرة، والتوأم من الذكور مسؤوليات كبرى تنتهي بنجاح.',
        warningMeaning: 'تتطلب الصبر وقوة التحمل لإدارة المسؤوليات الجديدة.',
        context: 'رؤية حركة الجنين في البطن بوضوح.'
      }
    ]
  }
};

export function enrichDreamArticle(dream: DreamTopic): DreamTopic {
  const custom = CUSTOM_DREAM_ENRICHMENTS[dream.slug];

  // الإثراء المعتمد على بيانات حقيقية فقط:
  // لا تُنشأ أقسام ولا نصوص ولا اقتباسات افتراضية بلا مادة موثقة،
  // فلا تظهر الأقسام إلا إذا كانت المادة الفعلية للرمز تسمح بذلك.
  const quickAnswer = custom?.quickAnswer ?? dream.quickAnswer;
  const scholarlyNuances = custom?.scholarlyNuances ?? dream.scholarlyNuances;
  const comparisonTable = custom?.comparisonTable ?? (dream.comparisonTable && dream.comparisonTable.length > 0 ? dream.comparisonTable : undefined);
  const practicalAdvice = custom?.practicalAdvice ?? (dream.practicalAdvice && dream.practicalAdvice.length > 0 ? dream.practicalAdvice : undefined);
  const commonMistakes = custom?.commonMistakes ?? (dream.commonMistakes && dream.commonMistakes.length > 0 ? dream.commonMistakes : undefined);

  // Expanded Ibn Sirin & Nabulsi if custom provided
  const ibnSirinInterpretation = custom?.expandedIbnSirin || dream.ibnSirinInterpretation;
  const nabulsiInterpretation = custom?.expandedNabulsi || dream.nabulsiInterpretation;

  const cases = {
    single: custom?.expandedCases?.single || dream.cases.single,
    married: custom?.expandedCases?.married || dream.cases.married,
    pregnant: custom?.expandedCases?.pregnant || dream.cases.pregnant,
    divorced: custom?.expandedCases?.divorced || dream.cases.divorced,
    man: custom?.expandedCases?.man || dream.cases.man,
  };

  return {
    ...dream,
    quickAnswer,
    scholarlyNuances,
    comparisonTable,
    practicalAdvice,
    commonMistakes,
    ibnSirinInterpretation,
    nabulsiInterpretation,
    cases
  };
}
